import React from 'react'
import { CellProps, Column } from 'react-table'
import Table from '../table'
import { WorkerCounterTemplate } from '../../api/worker-counter-templates'
import { ResourceLinkCell } from '../table/cells'
import { sortByIgnoreCase } from '../../utils/arrays'
import { formatDate } from '../../utils/date'
import Protected from '../protected/protected'
import { useLocation } from 'react-router-dom'

interface WorkerCounterTemplatesListProps {
  /** ID de l'employeur pour les liens de navigation */
  employerId: string
  /** Liste des modèles de compteurs à afficher */
  workerCounterTemplates: WorkerCounterTemplate[]
  /** Message affiché quand aucun compteur n'est disponible */
  noDataMessage?: string
}

/**
 * Composant pour afficher la liste des modèles de compteurs de travailleurs
 * Affiche un tableau avec les informations des compteurs pour un employeur donné
 */
const WorkerCounterTemplatesList: React.FC<WorkerCounterTemplatesListProps> = ({
  employerId,
  workerCounterTemplates,
  noDataMessage = 'Aucun compteur à afficher',
}) => {
  const location = useLocation()
  const basePath = location.pathname.includes('/employers/')
    ? `/employers/${employerId}`
    : '/settings'
  // Configuration des colonnes du tableau
  const tableColumns = useWorkerCounterTemplatesTableColumns(basePath)

  return (
    <Table
      columns={tableColumns}
      data={sortByIgnoreCase<WorkerCounterTemplate>(workerCounterTemplates || [], 'name')}
      noDataMessage={noDataMessage}
    />
  )
}

/**
 * Hook pour configurer les colonnes du tableau des modèles de compteurs
 * @param basePath - Base path pour les liens de navigation
 * @returns Configuration des colonnes du tableau
 */
const useWorkerCounterTemplatesTableColumns = (
  basePath: string,
): Column<WorkerCounterTemplate>[] => {
  return React.useMemo(
    () => [
      {
        Header: 'Nom',
        accessor: 'name',
        Cell: data => (
          <>
            <Protected roles={['superAdmin']}>
              <ResourceLinkCell
                resource={data.row.original}
                path={`${basePath}/worker-counters/`}
                label={data.row.original.name}
              />
            </Protected>
            <Protected roles={['employerMember']}>{data.row.original.name}</Protected>
          </>
        ),
      },
      {
        Header: 'Description',
        accessor: 'description',
        Cell: data => data.row.original.description || '-',
      },
      {
        Header: 'Règles comptabilisées',
        accessor: 'employerRules',
        Cell: data => {
          const rules = data.row.original.employerRules
          if (!rules || rules.length === 0) return '0'
          return rules.map(rule => rule.name).join(' | ')
        },
      },
      {
        Header: 'Début période',
        accessor: 'usagePeriods',
        Cell: data => {
          const periods = data.row.original.usagePeriods
          if (!periods || periods.length === 0) return 'Non défini'
          return periods
            .map(period => formatDate(period.startDate))
            .map((date, index) => (
              <React.Fragment key={index}>
                {date}
                {index < periods.length - 1 && <br />}
              </React.Fragment>
            ))
        },
      },
      {
        Header: 'Fin période',
        // @ts-expect-error: react-table type
        accessor: 'usagePeriods.endDate',
        Cell: (data: CellProps<WorkerCounterTemplate>) => {
          const periods = data.row.original.usagePeriods as WorkerCounterTemplate['usagePeriods']
          if (!periods || periods.length === 0) return 'Non défini'
          return periods
            .map(period => (period.endDate ? formatDate(period.endDate) : 'N/A'))
            .map((date, index) => (
              <React.Fragment key={index}>
                {date}
                {index < periods.length - 1 && <br />}
              </React.Fragment>
            ))
        },
      },
    ],
    [basePath],
  )
}

export default WorkerCounterTemplatesList
